import { makeAutoObservable, runInAction } from "mobx";
import { notificationsClient } from "@/shared/rpc/clients";
import { NotificationEntityType, NotificationType } from "@cronika/proto/common/notification_pb";

/**
 * Transport-agnostic view of a `common.NotificationMessage`. Only the generic
 * envelope lives here (type, entity pointer, read state, time); `payload` stays
 * opaque and is interpreted per `type` by a presenter (see
 * notification-presenters). Adding a notification kind never touches the store.
 */
export interface AppNotification {
  id: string;
  type: NotificationType;
  entityType: NotificationEntityType;
  entityId: string | null;
  payload: Record<string, unknown>;
  read: boolean;
  timestamp: number;
}

const parsePayload = (raw?: string): Record<string, unknown> => {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

class NotificationsStore {
  items: AppNotification[] = [];
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  get unreadCount() {
    return this.items.filter((n) => !n.read).length;
  }

  async loadNotifications() {
    if (this.loading) return;
    runInAction(() => {
      this.loading = true;
    });
    try {
      const res = await notificationsClient.listNotifications({ pagination: { page: 1, size: 20 } });
      runInAction(() => {
        this.items = (res.data ?? []).map(
          (n): AppNotification => ({
            id: n.id,
            type: n.type,
            entityType: n.entityType,
            entityId: n.entityId ?? null,
            payload: parsePayload(n.payload),
            read: n.isRead,
            timestamp: n.createdAt ? new Date(n.createdAt).getTime() : Date.now(),
          }),
        );
      });
    } catch (e) {
      console.error("Failed to load notifications", e);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async markRead(ids: string[]) {
    const target = this.items.filter((n) => ids.includes(n.id) && !n.read);
    if (target.length === 0) return;

    runInAction(() => {
      target.forEach((n) => (n.read = true));
    });

    try {
      await notificationsClient.markNotificationsRead({ ids: target.map((n) => n.id) });
    } catch (e) {
      console.error("Failed to mark notifications read", e);
    }
  }

  async markAllRead() {
    if (!this.items.some((n) => !n.read)) return;

    runInAction(() => {
      this.items.forEach((n) => (n.read = true));
    });

    try {
      await notificationsClient.markAllNotificationsRead({});
    } catch (e) {
      console.error("Failed to mark all notifications read", e);
    }
  }

  clear() {
    runInAction(() => {
      this.items = [];
    });
  }
}

export const notificationsStore = new NotificationsStore();